import { Shadow, Text, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Service } from '@genial/services';
import { useNavigation } from '@react-navigation/native';
import React, { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useAbsoluteRoute } from '../../../../hooks/useAbsoluteRoute';
import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import {
  advisorIdToApisSelector,
  partnerIdToApisSelector,
} from '../../../../store/master/selector';
import { useTransactionFlowActions } from '../../../../store/transactionFlow/actions';
import { historicalSearchQuery } from '../../../../store/transactionFlow/selector';
import { FeatureToggle, useUnleashFlags } from '../../../../utils/useUnleashFlags';

import { FilterModalTypes, Filters } from './Filters';
import { TransactionTable } from './TransactionTable';

interface HistoricalSearchProps {}

export const HistoricalSearch: React.FC<HistoricalSearchProps> = () => {
  const [filters, setFilters] = useState<FilterModalTypes>({
    initialDate: null,
    finalDate: null,
    productType: null,
  });
  const currentRoute = useAbsoluteRoute();
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const { b2bShowModuleCustomers } = useUnleashFlags(FeatureToggle.Customers);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const { downloadTransactionFlowHistory } = useTransactionFlowActions();

  const defaultInitialDate = new Date(new Date().setDate(new Date().getDate() - 30));
  const defaultFinalDate = new Date();

  const handleFilterChange = (filterSelected: FilterModalTypes) => {
    setFilters(filterSelected);
  };

  const handleDownloadExcel = () => {
    downloadTransactionFlowHistory(
      filters.initialDate || defaultInitialDate,
      filters.finalDate || defaultFinalDate,
      filters.productType || 0
    );
  };

  const handleSelectAccount = async (cpfCnpj: string) => {
    if (!cpfCnpj) {
      return;
    }

    const { data: customers } = await Service.Customer.V1.getByCpfCnpj({
      cpfCnpj,
      partnerId: selectedPartner,
      advisorId: selectedAdvisor,
      screenName: 'transaction-flow-search-historical',
    });

    if (!customers) {
      return;
    }

    const customer = customers[0].customer;

    if (b2bShowModuleCustomers) {
      const method =
        currentRoute.name === 'AngularFrame_CustomerDetail'
          ? navigation.replace
          : navigation.navigate;
      method('AppDrawer', {
        screen: 'AngularFrame_CustomerDetail',
        params: {
          state: 'app.posicaoOnline.posicao',
          DocumentoCliente: customer.cpfCnpjText,
          IdConta: customer.accountId,
        },
      });
    } else {
      navigation.navigate('AppDrawer', {
        screen: 'CustomerDetail',
        params: {
          customerId: customer.customerId,
          accountNumber: customer.accountNumber,
          accountId: customer.accountId,
          cpfCnpj: customer.cpfCnpjText,
        },
      });
    }
  };

  const queryParams = useMemo(() => {
    const params = {
      initialDate: filters.initialDate || defaultInitialDate,
      endDate: filters.finalDate || defaultFinalDate,
      productType: filters.productType || 0,
    };

    return params;
  }, [filters]);

  const {
    data: historicalData,
    error,
    loading,
    refresher,
  } = useRecoilQuery(historicalSearchQuery(queryParams));

  return (
    <Shadow level={1} borderRadius={BoxRadius.sm} padding={Spacing['inset.xs']}>
      <VStack
        space={{ base: Spacing['stack.xxxs'], lg: Spacing['stack.xxs'] }}
        pb={Spacing['stack.xs']}>
        <Text fontSize={Typography['fontsize.md']} variant="semibold" color="neutral.dark">
          HISTÓRICO DE MOVIMENTAÇÕES
        </Text>
        <Filters
          onFilterChange={handleFilterChange}
          onDownload={handleDownloadExcel}
          loading={loading}
        />
        <TransactionTable
          data={historicalData?.data}
          error={!!error}
          loading={loading}
          onErrorRefresh={refresher}
          onSelectAccount={handleSelectAccount}
        />
      </VStack>
    </Shadow>
  );
};
